document.documentElement.classList.add('zyj-dlugo-template');

if (!customElements.get('zyj-dosage-calculator')) {
  customElements.define(
    'zyj-dosage-calculator',
    class ZyjDosageCalculator extends HTMLElement {
      connectedCallback() {
        this.options = [...this.querySelectorAll('[data-zyj-dose]')];
        this.days = this.querySelector('[data-zyj-days]');
        this.chews = this.querySelector('[data-zyj-chews]');
        this.cost = this.querySelector('[data-zyj-cost]');
        this.productInfo = this.closest('product-info') || document;
        this.purchaseInputs = [...this.productInfo.querySelectorAll('[data-zyj-purchase]')];
        this.currencyFormatter = new Intl.NumberFormat(this.dataset.locale || 'pl-PL', {
          style: 'currency',
          currency: this.dataset.currency || 'PLN',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

        this.options.forEach((option) => {
          option.addEventListener('click', () => {
            this.options.forEach((item) => item.setAttribute('aria-pressed', 'false'));
            option.setAttribute('aria-pressed', 'true');
            this.update();
          });
        });

        this.purchaseInputs.forEach((input) => input.addEventListener('change', () => this.update()));
        this.update();
      }

      update() {
        const selectedDose = this.options.find((option) => option.getAttribute('aria-pressed') === 'true') || this.options[0];
        const dose = Number(selectedDose?.dataset.zyjDose || 1);
        const doseLabel = selectedDose?.dataset.zyjDoseLabel || String(dose);
        const packageCount = Number(this.dataset.packageCount || 1);
        const days = Math.max(1, Math.floor(packageCount / Math.max(1, dose)));
        const selectedPurchase = this.purchaseInputs.find((input) => input.checked);
        const priceInCents = Number(selectedPurchase?.dataset.price || this.dataset.basePrice || 0);

        if (this.days) this.days.textContent = String(days);
        if (this.chews) this.chews.textContent = doseLabel;
        if (this.cost) this.cost.textContent = this.currencyFormatter.format(priceInCents / 100 / days);

        this.dataset.weight = selectedDose?.dataset.label || '';
        document.dispatchEvent(new CustomEvent('zyj:dosage-change', {
          detail: { days, dose, doseLabel, weight: this.dataset.weight },
        }));
      }
    }
  );
}

if (!customElements.get('zyj-purchase-options')) {
  customElements.define(
    'zyj-purchase-options',
    class ZyjPurchaseOptions extends HTMLElement {
      connectedCallback() {
        this.inputs = [...this.querySelectorAll('[data-zyj-purchase]')];
        this.form = this.closest('form');
        this.mainSellingPlan = this.form?.querySelector('[data-zyj-main-selling-plan]') || this.querySelector('[data-zyj-main-selling-plan]');
        this.prepareForm(this.form, this.mainSellingPlan);
        document.querySelectorAll('[data-zyj-sticky-selling-plan]').forEach((stickyInput) => {
          this.prepareForm(stickyInput.closest('form'), stickyInput);
        });
        this.inputs.forEach((input) => input.addEventListener('change', () => this.syncStickyBar(input)));
        const selected = this.inputs.find((input) => input.checked);
        if (selected) this.syncStickyBar(selected);
      }

      prepareForm(form, sellingPlanInput) {
        if (!form || !sellingPlanInput || form.dataset.zyjSellingPlanReady === 'true') return;
        form.dataset.zyjSellingPlanReady = 'true';
        form.addEventListener('submit', () => {
          if (sellingPlanInput.value && !sellingPlanInput.disabled) {
            sellingPlanInput.name = 'selling_plan';
          } else {
            sellingPlanInput.removeAttribute('name');
          }
        }, true);
      }

      syncStickyBar(input) {
        const sellingPlanId = input.dataset.sellingPlanId || '';
        if (this.mainSellingPlan) {
          this.mainSellingPlan.value = sellingPlanId;
          this.mainSellingPlan.disabled = !sellingPlanId;
        }

        document.querySelectorAll('[data-zyj-sticky-selling-plan]').forEach((stickyInput) => {
          stickyInput.value = sellingPlanId;
          stickyInput.disabled = !sellingPlanId;
        });
      }
    }
  );
}

if (!customElements.get('zyj-report')) {
  customElements.define(
    'zyj-report',
    class ZyjReport extends HTMLElement {
      connectedCallback() {
        this.counters = [...this.querySelectorAll('[data-count-target]')];
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (this.reducedMotion || !('IntersectionObserver' in window)) {
          this.show();
          return;
        }

        this.counters.forEach((counter) => { counter.textContent = '0'; });
        this.observer = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.show();
          this.observer?.disconnect();
        }, { threshold: 0.12 });
        this.observer.observe(this);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      show() {
        this.classList.add('is-visible');
        if (this.reducedMotion) return;

        const start = performance.now();
        const duration = 3000;

        const tick = (time) => {
          const progress = Math.min(1, (time - start) / duration);
          const eased = 1 - Math.pow(1 - progress, 3);

          this.counters.forEach((counter) => {
            const target = Number(counter.dataset.countTarget || 0);
            counter.textContent = String(Math.round(target * eased));
          });

          if (progress < 1) window.requestAnimationFrame(tick);
        };

        window.requestAnimationFrame(tick);
      }
    }
  );
}

if (!customElements.get('zyj-story-gallery')) {
  customElements.define(
    'zyj-story-gallery',
    class ZyjStoryGallery extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-story-track]');
        this.slides = [...this.querySelectorAll('.zyj-story-gallery__slide')];
        this.progress = this.querySelector('[data-story-progress]');
        this.progressBar = this.querySelector('[data-story-progress-bar]');
        if (!this.track || !this.progress || !this.progressBar || !this.slides.length) return;

        this.update = this.update.bind(this);
        this.track.addEventListener('scroll', this.update, { passive: true });
        window.addEventListener('resize', this.update);
        this.update();
      }

      disconnectedCallback() {
        this.track?.removeEventListener('scroll', this.update);
        window.removeEventListener('resize', this.update);
      }

      update() {
        const maxScroll = Math.max(1, this.track.scrollWidth - this.track.clientWidth);
        const scrollProgress = Math.min(1, Math.max(0, this.track.scrollLeft / maxScroll));
        const activeIndex = Math.min(this.slides.length - 1, Math.round(scrollProgress * (this.slides.length - 1)));

        this.progressBar.style.width = `${100 / this.slides.length}%`;
        this.progressBar.style.transform = `translateX(${scrollProgress * (this.slides.length - 1) * 100}%)`;
        this.progress.setAttribute('aria-valuemax', String(this.slides.length));
        this.progress.setAttribute('aria-valuenow', String(activeIndex + 1));
        this.slides.forEach((slide, index) => slide.classList.toggle('is-active', index === activeIndex));
      }
    }
  );
}

if (!customElements.get('zyj-treat-motion')) {
  customElements.define(
    'zyj-treat-motion',
    class ZyjTreatMotion extends HTMLElement {
      connectedCallback() {
        this.treats = [...this.querySelectorAll('.zyj-treat')];
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!this.treats.length || this.reducedMotion) return;

        this.requestUpdate = this.requestUpdate.bind(this);
        this.update = this.update.bind(this);
        window.addEventListener('scroll', this.requestUpdate, { passive: true });
        window.addEventListener('resize', this.requestUpdate);
        this.update();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.requestUpdate);
        window.removeEventListener('resize', this.requestUpdate);
        if (this.frame) window.cancelAnimationFrame(this.frame);
      }

      requestUpdate() {
        if (this.frame) return;
        this.frame = window.requestAnimationFrame(this.update);
      }

      update() {
        this.frame = undefined;
        const rect = this.getBoundingClientRect();
        const travel = window.innerHeight + rect.height;
        const progress = Math.min(1, Math.max(0, (window.innerHeight - rect.top) / travel));

        this.treats.forEach((treat) => {
          const x = Number(treat.dataset.moveX || 0) * progress;
          const y = Number(treat.dataset.moveY || 0) * progress;
          const rotation = Number(treat.dataset.moveRotate || 0) * progress;
          treat.style.setProperty('--motion-x', `${x.toFixed(2)}px`);
          treat.style.setProperty('--motion-y', `${y.toFixed(2)}px`);
          treat.style.setProperty('--motion-rotate', `${rotation.toFixed(2)}deg`);
        });
      }
    }
  );
}

if (!customElements.get('zyj-benefit-motion')) {
  customElements.define(
    'zyj-benefit-motion',
    class ZyjBenefitMotion extends HTMLElement {
      connectedCallback() {
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (this.reducedMotion || !('IntersectionObserver' in window)) {
          this.classList.add('is-in-view');
          return;
        }

        this.observer = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.classList.add('is-in-view');
          this.observer?.disconnect();
        }, { threshold: .42 });
        this.observer.observe(this);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }
    }
  );
}

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-zyj-scroll-to-buy]');
  if (!trigger) return;
  event.preventDefault();
  const targetSelector = trigger.getAttribute('href');
  const target = targetSelector?.startsWith('#') ? document.querySelector(targetSelector) : null;
  const product = target || document.querySelector('.zyj-dlugo-product');
  if (product) product.scrollIntoView({ behavior: 'smooth', block: 'center' });
});

const initZyjGalleryProgress = () => {
  document.querySelectorAll('.zyj-dlugo-product media-gallery').forEach((gallery) => {
    const progress = gallery.querySelector('[data-zyj-gallery-progress]');
    const bar = gallery.querySelector('[data-zyj-gallery-progress-bar]');
    const list = gallery.querySelector('.product__media-list');
    const total = Number(gallery.dataset.totalMediaCount || 0);
    if (!progress || !bar || !list || total < 2) return;

    const update = () => {
      const slides = [...list.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
      const active = Math.max(0, slides.findIndex((slide) => slide.classList.contains('swiper-slide-active')));
      bar.style.width = `${100 / total}%`;
      bar.style.transform = `translateX(${active * 100}%)`;
      progress.setAttribute('aria-valuenow', String(active + 1));
    };

    new MutationObserver(update).observe(list, { subtree: true, attributes: true, attributeFilter: ['class'] });
    update();
  });
};

const initZyjImageZoom = (root = document) => {
  const frames = [...root.querySelectorAll('[data-zyj-image-zoom]:not([data-zyj-image-zoom-ready])')];
  if (!frames.length) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  frames.forEach((frame) => { frame.dataset.zyjImageZoomReady = 'true'; });

  if (reducedMotion || !('IntersectionObserver' in window)) {
    frames.forEach((frame) => frame.classList.add('is-zoomed'));
    return;
  }

  const observer = new IntersectionObserver((entries, imageObserver) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-zoomed');
      imageObserver.unobserve(entry.target);
    });
  }, { threshold: 0.42 });

  frames.forEach((frame) => observer.observe(frame));
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initZyjGalleryProgress();
    initZyjImageZoom();
  }, { once: true });
} else {
  initZyjGalleryProgress();
  initZyjImageZoom();
}

document.addEventListener('shopify:section:load', (event) => initZyjImageZoom(event.target));

const initZyjPurchaseFixes = () => {
  if (!document.documentElement.classList.contains('zyj-dlugo-template')) return;

  const syncSellingPlans = () => {
    const selected = document.querySelector('[data-zyj-purchase]:checked');
    const sellingPlanId = selected?.dataset.sellingPlanId?.trim() || '';
    const forms = [...document.querySelectorAll('form[action*="/cart/add"]')].filter((form) => {
      return form.closest('.zyj-dlugo-product, .sticky-atc-bar')
        || form.querySelector('[data-zyj-main-selling-plan], [data-zyj-sticky-selling-plan]');
    });

    forms.forEach((form) => {
      const inputs = [...form.querySelectorAll(
        'input[name="selling_plan"], [data-zyj-main-selling-plan], [data-zyj-sticky-selling-plan]'
      )];

      inputs.forEach((input) => {
        input.value = '';
        input.disabled = true;
        input.removeAttribute('name');
      });

      if (!sellingPlanId) return;

      let input = inputs.find((item) => {
        return item.hasAttribute('data-zyj-main-selling-plan')
          || item.hasAttribute('data-zyj-sticky-selling-plan');
      });

      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        form.appendChild(input);
      }

      input.name = 'selling_plan';
      input.value = sellingPlanId;
      input.disabled = false;
    });
  };

  if (document.documentElement.dataset.zyjPurchaseFixesReady !== 'true') {
    document.documentElement.dataset.zyjPurchaseFixesReady = 'true';

    document.addEventListener('change', (event) => {
      if (event.target.matches('[data-zyj-purchase]')) syncSellingPlans();
    }, true);

    document.addEventListener('submit', (event) => {
      if (event.target.matches('form[action*="/cart/add"]')) syncSellingPlans();
    }, true);
  }

  document.querySelectorAll('.sticky-atc-bar__form button[type="submit"]').forEach((button) => {
    if (button.dataset.zyjStickyFixReady === 'true') return;
    button.dataset.zyjStickyFixReady = 'true';

    button.addEventListener('click', (event) => {
      const mainButton = document.querySelector(
        '.zyj-dlugo-product form[action*="/cart/add"] button[type="submit"][name="add"]'
      );

      if (!mainButton || mainButton === button) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      syncSellingPlans();
      mainButton.form?.requestSubmit(mainButton);
    }, true);
  });

  syncSellingPlans();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initZyjPurchaseFixes, { once: true });
} else {
  initZyjPurchaseFixes();
}

document.addEventListener('shopify:section:load', initZyjPurchaseFixes);

if (document.documentElement.dataset.zyjStickyDelegatedReady !== 'true') {
  document.documentElement.dataset.zyjStickyDelegatedReady = 'true';

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('.sticky-atc-bar__form button[type="submit"]');
    if (!button || !document.documentElement.classList.contains('zyj-dlugo-template')) return;

    const mainButton = document.querySelector(
      '.zyj-dlugo-product form[action*="/cart/add"] button[type="submit"][name="add"]'
    );
    if (!mainButton || mainButton === button) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    const form = mainButton.form;
    if (!form) return;

    const selected = document.querySelector('[data-zyj-purchase]:checked');
    const sellingPlanId = selected?.dataset.sellingPlanId?.trim() || '';
    const planInputs = [...form.querySelectorAll(
      'input[name="selling_plan"], [data-zyj-main-selling-plan]'
    )];

    planInputs.forEach((input) => {
      input.value = '';
      input.disabled = true;
      input.removeAttribute('name');
    });

    if (sellingPlanId) {
      let planInput = planInputs[0];
      if (!planInput) {
        planInput = document.createElement('input');
        planInput.type = 'hidden';
        form.appendChild(planInput);
      }
      planInput.name = 'selling_plan';
      planInput.value = sellingPlanId;
      planInput.disabled = false;
    }

    const shopRoot = window.Shopify?.routes?.root || '/';
    const addUrl = shopRoot.replace(/\/?$/, '/') + 'cart/add.js';
    const formData = new FormData(form);

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    fetch(addUrl, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    })
      .then((response) => {
        if (!response.ok) throw new Error('Unable to add product to cart');
        return response.json();
      })
      .then(() => window.location.assign(shopRoot + 'cart'))
      .catch(() => {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        form.requestSubmit(mainButton);
      });
  }, true);
}
